import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../Context/AuthContext";
import { Base_URL } from "../url";
import "../Styling/BankManagement.css";

const PAGE_SIZE = 30;

const BankManagement = () => {
  const { user } = useAuth();

  // ── existing state ──────────────────────────────────────────────────────────
  const [countryCode, setCountryCode] = useState("IN");
  const [countryList, setCountryList] = useState([]);
  const [bankList, setBankList] = useState([]);
  const [selectedBanks, setSelectedBanks] = useState([]);
  const [savedBanks, setSavedBanks] = useState([]);

  // ── search + pagination ─────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // ── loading states ──────────────────────────────────────────────────────────
  const [banksLoading, setBanksLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  // ── abort controller ref — cancels stale in-flight requests ────────────────
  const abortRef = useRef(null);


  // ==============================
  // 1️⃣ Fetch Country Codes  (UNCHANGED logic)
  // ==============================
  useEffect(() => {
    const fetchCountryCodes = async () => {
      try {
        const response = await axios.get(
          `${Base_URL}/BankCountryMaster/GetCountryCodes`
        );
        if (response.data.success === true) {
          setCountryList(response.data.data);
        } else {
          setCountryList([]);
        }
      } catch (error) {
        console.error("Error fetching country codes:", error);
        setCountryList([]);
      }
    };
    fetchCountryCodes();
  }, []);


  // ==============================
  // Debounce search — fires fetch only after 400ms of no typing
  // ==============================
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);


  // ==============================
  // 2️⃣ Fetch Banks — fires on debouncedSearch / page / country change only.
  //    savedBanks removed from deps (backend already excludes them).
  //    AbortController cancels any stale in-flight request before the new one fires.
  // ==============================
  const fetchBanks = useCallback(async (page, search, country) => {
    if (!country) {
      setBankList([]);
      setTotalCount(0);
      setTotalPages(1);
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();

    setBanksLoading(true);
    try {
      const response = await axios.get(
        `${Base_URL}/BankCountryMaster/GetBanksByCountry`,
        {
          signal: abortRef.current.signal,
          params: {
            countryCode: country,
            page,
            pageSize: PAGE_SIZE,
            search,
            orgid: user?.organisationId,
          },
        }
      );
      if (response.data.success) {
        setBankList(response.data.data);
        setTotalCount(response.data.totalCount);
        setTotalPages(response.data.totalPages);
      } else {
        setBankList([]);
        setTotalCount(0);
        setTotalPages(1);
      }
    } catch (error) {
      if (axios.isCancel(error) || error?.code === "ERR_CANCELED") return;
      console.error("Error fetching banks:", error);
      setBankList([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setBanksLoading(false);
    }
  }, [user?.organisationId]);

  useEffect(() => {
    fetchBanks(currentPage, debouncedSearch, countryCode);
  }, [countryCode, currentPage, debouncedSearch]); // savedBanks intentionally removed


  // Reset pagination + search whenever country changes
  useEffect(() => {
    setCurrentPage(1);
    setSearchQuery("");
    setDebouncedSearch("");
  }, [countryCode]);


  // ==============================
  // Fetch Saved Banks  (UNCHANGED logic)
  // ==============================
  const fetchSavedBanks = useCallback(async () => {
    try {
      const response = await axios.get(
        `${Base_URL}/BankCountryMaster/GetOrgBankMapping`,
        { params: { orgid: user.organisationId } }
      );
      if (response.data.success) {
        setSavedBanks(response.data.data);
      } else {
        setSavedBanks([]);
      }
    } catch (error) {
      console.error("Error fetching saved banks:", error);
      setSavedBanks([]);
    }
  }, [user]);

  useEffect(() => {
    if (user?.organisationId) fetchSavedBanks();
  }, [user]);


  // ==============================
  // Derived: filter already-selected banks out of the paged list  (UNCHANGED logic)
  // ==============================
  const pagedBanks = useMemo(() => {
    return bankList.filter(
      (bankItem) =>
        !selectedBanks.some(
          (s) => s.bankCountryMasterGuid === bankItem.bankCountryMasterGuid
        )
    );
  }, [bankList, selectedBanks]);

  const safePage = currentPage;


  // ==============================
  // 3️⃣ Toggle bank checkbox  (UNCHANGED logic)
  // ==============================
  const toggleBankSelection = (bankItem) => {
    const alreadySelected = selectedBanks.some(
      (s) => s.bankCountryMasterGuid === bankItem.bankCountryMasterGuid
    );
    if (alreadySelected) {
      setSelectedBanks((prev) =>
        prev.filter(
          (s) => s.bankCountryMasterGuid !== bankItem.bankCountryMasterGuid
        )
      );
    } else {
      setSelectedBanks((prev) => [...prev, bankItem]);
    }
  };


  // ==============================
  // 4️⃣ Remove Bank from selected tags  (UNCHANGED logic)
  // ==============================
  const removeBank = (bankToRemove) => {
    setSelectedBanks((prev) =>
      prev.filter(
        (item) =>
          item.bankCountryMasterGuid !== bankToRemove.bankCountryMasterGuid
      )
    );
  };


  // ==============================
  // 5️⃣ Submit — optimistic UI: chips move to View Banks instantly.
  //    API runs in background. Bank list re-fetches after save.
  //    Rolls back on API failure.
  // ==============================
  const handleSubmit = async () => {
    if (selectedBanks.length === 0 || submitLoading) return;

    const banksBeingSubmitted = [...selectedBanks];

    // Optimistic: clear selected chips + add to savedBanks immediately
    setSelectedBanks([]);
    setSavedBanks((prev) => {
      const incoming = banksBeingSubmitted.filter(
        (b) => !prev.some((s) => s.bankCountryMasterGuid === b.bankCountryMasterGuid)
      );
      return [...prev, ...incoming];
    });

    setSubmitLoading(true);
    try {
      const payload = banksBeingSubmitted.map((bank) => ({
        orgid: user.organisationId,
        bankMappingGuid: bank.bankCountryMasterGuid,
        isActive: false,
      }));

      await axios.post(
        `${Base_URL}/BankCountryMaster/SaveOrgBankMapping`,
        payload
      );

      // Authoritative re-sync of both lists after API confirms
      fetchSavedBanks();
      fetchBanks(currentPage, debouncedSearch, countryCode);
    } catch (error) {
      console.error("Error saving banks:", error);
      // Roll back optimistic update
      setSelectedBanks(banksBeingSubmitted);
      setSavedBanks((prev) =>
        prev.filter(
          (s) =>
            !banksBeingSubmitted.some(
              (b) => b.bankCountryMasterGuid === s.bankCountryMasterGuid
            )
        )
      );
    } finally {
      setSubmitLoading(false);
    }
  };


  // ==============================
  // 6️⃣ Deactivate — optimistic UI: chip removed instantly.
  //    API runs in background. Rolls back on failure.
  // ==============================
  const handleDeactivateBank = async (bankGuid) => {
    // Optimistic: remove from View Banks immediately
    setSavedBanks((prev) =>
      prev.filter((b) => b.bankCountryMasterGuid !== bankGuid)
    );

    try {
      await axios.post(
        `${Base_URL}/BankCountryMaster/DeactivateOrgBank`,
        null,
        { params: { bankGuid, orgid: user.organisationId } }
      );
      // Authoritative re-sync in background
      fetchSavedBanks();
    } catch (error) {
      console.error("Error deactivating bank:", error);
      // Roll back optimistic update
      fetchSavedBanks();
    }
  };


  // ── helpers ─────────────────────────────────────────────────────────────────
  const isChecked = (bankItem) =>
    selectedBanks.some(
      (s) => s.bankCountryMasterGuid === bankItem.bankCountryMasterGuid
    );

  const clearAll = () => setSelectedBanks([]);

  const goPage = (p) => {
    if (p < 1 || p > totalPages) return;
    setCurrentPage(p);
  };

  // Build visible page numbers (max 5 around current) — UNCHANGED
  const getPageNumbers = () => {
    const pages = [];
    const half = 2;
    let start = Math.max(1, safePage - half);
    let end = Math.min(totalPages, start + 4);
    if (end - start < 4) start = Math.max(1, end - 4);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };


  // ── render ──────────────────────────────────────────────────────────────────
  return (
    <div className="bm-container">

      {/* ── Title ── */}
      <div className="bm-title-card">
        <h2>Bank Management</h2>
        <p>Select and manage banks by country</p>
      </div>

      {/* ── Filter row ── */}
      <div className="bm-filter-card">
        <div className="bm-filter-row">

          {/* Country Code — UNCHANGED value/onChange */}
          <div className="bm-field-group">
            <label>Country Code</label>
            <select
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value)}
              className="bm-select"
            >
              {countryList.map((code, index) => (
                <option key={index} value={code}>{code}</option>
              ))}
            </select>
          </div>

          {/* Search — input updates instantly (display), API fires after 400ms debounce */}
          <div className="bm-field-group bm-search-group">
            <label>Search Banks</label>
            <div className="bm-search-wrap">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2.5">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                className="bm-search-input"
                placeholder="Search by bank name…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

        </div>
      </div>

      {/* ── Selected Banks tags ── */}
      <div className="bm-selected-card">
        <div className="bm-selected-header">
          <label className="bm-section-label">Selected Banks</label>
          {selectedBanks.length > 0 && (
            <span className="bm-badge">{selectedBanks.length}</span>
          )}
        </div>

        <div className="bm-chip-wrapper">
          {selectedBanks.length === 0 ? (
            <span className="bm-empty-hint">
              No banks selected yet — choose from the list below
            </span>
          ) : (
            selectedBanks.map((item) => (
              <div key={item.bankCountryMasterGuid} className="bm-chip">
                <span>{item.bankName} ({item.countryCode})</span>
                <span
                  className="bm-chip-close"
                  onClick={() => removeBank(item)}
                  title="Remove"
                >
                  ✕
                </span>
              </div>
            ))
          )}
        </div>
      </div>

      {/* ── Bank Checkbox List ── */}
      <div className="bm-banklist-card">

        <div className="bm-banklist-header">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h3>Available Banks</h3>
            <span className="bm-badge bm-badge-gray">
              {banksLoading ? "Loading…" : `Page ${safePage} of ${totalPages}`}
            </span>
          </div>
          <span className="bm-pagination-info">
            {banksLoading
              ? ""
              : totalCount === 0
              ? "No banks found"
              : `Showing ${(safePage - 1) * PAGE_SIZE + 1}–${Math.min(safePage * PAGE_SIZE, totalCount)} of ${totalCount}`}
          </span>
        </div>

        {/* Grid of checkboxes */}
        <div className={`bm-checkbox-grid${banksLoading ? " bm-loading" : ""}`}>
          {banksLoading ? (
            <div className="bm-no-results">Loading banks…</div>
          ) : pagedBanks.length === 0 ? (
            <div className="bm-no-results">No banks match your search.</div>
          ) : (
            pagedBanks.map((bankItem) => {
              const checked = isChecked(bankItem);
              return (
                <label
                  key={bankItem.bankCountryMasterid}
                  className={`bm-bank-item${checked ? " bm-bank-item--checked" : ""}`}
                  onClick={() => toggleBankSelection(bankItem)}
                >
                  <span className="bm-checkmark">
                    {checked && (
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
                        stroke="white" strokeWidth="3.5">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </span>
                  <span className="bm-bank-name">{bankItem.bankName}</span>
                </label>
              );
            })
          )}
        </div>

        {/* Pagination bar — UNCHANGED */}
        <div className="bm-pagination-bar">
          <div className="bm-pagination-controls">
            <button
              className="bm-page-btn"
              onClick={() => goPage(safePage - 1)}
              disabled={safePage === 1 || banksLoading}
            >&#8249;</button>

            {getPageNumbers()[0] > 1 && (
              <>
                <button className="bm-page-btn" onClick={() => goPage(1)}>1</button>
                {getPageNumbers()[0] > 2 && <span className="bm-ellipsis">…</span>}
              </>
            )}

            {getPageNumbers().map((p) => (
              <button
                key={p}
                className={`bm-page-btn${p === safePage ? " bm-page-btn--active" : ""}`}
                onClick={() => goPage(p)}
              >{p}</button>
            ))}

            {getPageNumbers().at(-1) < totalPages && (
              <>
                {getPageNumbers().at(-1) < totalPages - 1 && (
                  <span className="bm-ellipsis">…</span>
                )}
                <button className="bm-page-btn" onClick={() => goPage(totalPages)}>
                  {totalPages}
                </button>
              </>
            )}

            <button
              className="bm-page-btn"
              onClick={() => goPage(safePage + 1)}
              disabled={safePage === totalPages || banksLoading}
            >&#8250;</button>
          </div>
        </div>

        {/* Actions row */}
        <div className="bm-actions-row">
          <span className="bm-selection-summary">
            <strong>{selectedBanks.length}</strong> bank{selectedBanks.length !== 1 ? "s" : ""} selected
          </span>
          <div className="bm-actions-btns">
            <button className="bm-btn bm-btn-ghost" onClick={clearAll}>
              Clear All
            </button>
            <button
              className="bm-btn bm-btn-primary"
              onClick={handleSubmit}
              disabled={selectedBanks.length === 0 || submitLoading}
            >
              {submitLoading ? "Saving…" : "Submit Banks"}
            </button>
          </div>
        </div>

      </div>

      {/* ── View Banks ── */}
      <div className="bm-view-card">
        <div className="bm-view-header">
          <h3>View Banks</h3>
          {savedBanks.length > 0 && (
            <span className="bm-badge bm-badge-green">{savedBanks.length} saved</span>
          )}
        </div>

        {savedBanks.length === 0 ? (
          <div className="bm-no-banks">
            No active banks saved for this organisation.
          </div>
        ) : (
          <div className="bm-view-chip-wrapper">
            {savedBanks.map((bank) => (
              <div key={bank.bankCountryMasterGuid} className="bm-view-chip">
                <span className="bm-view-dot" />
                <span className="bm-view-bank-name">{bank.bankName}</span>
                <button
                  className="bm-view-remove-btn"
                  title="Remove bank"
                  onClick={() => handleDeactivateBank(bank.bankCountryMasterGuid)}
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="3">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
};

export default BankManagement;